import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Bell, CheckCheck, MessageSquare, ExternalLink, Trash2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadNotifications(); }, []);

  useEffect(() => {
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifications(data || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
    window.dispatchEvent(new Event("notif-changed"));
  };

  const markAllRead = async () => {
    await api.put("/notifications/read-all");
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    toast.success("All notifications marked as read");
    window.dispatchEvent(new Event("notif-changed"));
  };

  const handleClick = async (notif) => {
    if (!notif.is_read) await markRead(notif.id);
    navigate(`/jobs/${notif.job_card_id}`);
  };

  const deleteNotif = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      window.dispatchEvent(new Event("notif-changed"));
    } catch { toast.error("Failed to delete"); }
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  if (loading) return <div className="text-slate-500 p-8">Loading notifications...</div>;

  return (
    <div data-testid="notifications-page" className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900 flex items-center gap-2">
            <Bell size={24} className="text-teal-600" /> Notifications
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            data-testid="mark-all-read-btn"
            onClick={markAllRead}
            variant="outline"
            size="sm"
            className="border-slate-300 text-slate-600"
          >
            <CheckCheck size={14} className="mr-2" /> Mark All Read
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Bell size={36} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No notifications yet</p>
          <p className="text-slate-400 text-xs mt-1">Notes on your job cards will appear here</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map((n) => (
            <button
              key={n.id}
              data-testid={`notif-${n.id}`}
              onClick={() => handleClick(n)}
              className={`w-full text-left bg-white border rounded-xl p-4 transition-all hover:shadow-sm hover:border-teal-200 group
                ${n.is_read
                  ? "border-slate-200"
                  : "border-teal-300 bg-teal-50/30"
                }`}
            >
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 w-8 h-8 rounded-xl flex items-center justify-center shrink-0
                  ${n.is_read ? "bg-slate-100" : "bg-teal-100"}`}>
                  <MessageSquare size={14} className={n.is_read ? "text-slate-400" : "text-teal-700"} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-sm font-medium ${n.is_read ? "text-slate-600" : "text-slate-900"}`}>
                      {n.created_by_name}
                    </span>
                    <Badge variant="outline" className="text-[9px] uppercase tracking-wider bg-slate-50 text-slate-500 border-slate-200">
                      {n.created_by_role}
                    </Badge>
                    <span className="text-[10px] text-slate-400 ml-auto shrink-0">{timeAgo(n.timestamp)}</span>
                  </div>
                  <p className={`text-sm leading-snug ${n.is_read ? "text-slate-500" : "text-slate-700"}`}>
                    {n.message}
                  </p>
                  <div className="flex items-center gap-1 mt-2 text-teal-600 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    <ExternalLink size={12} /> Open Job Card {n.job_card_number}
                  </div>
                </div>
                {!n.is_read ? (
                  <div className="w-2 h-2 rounded-full bg-teal-500 mt-2 shrink-0" />
                ) : (
                  <button
                    data-testid={`delete-notif-${n.id}`}
                    onClick={(e) => deleteNotif(e, n.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500 p-1 shrink-0"
                    title="Delete notification"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
